<template>
    <div>
        <!-- Filtre par dates (sauf contexte fiche cheval) -->
        <DateRange
            v-if="!horse_id"
            :start.sync="start_date"
            :end.sync="end_date"
            @submit="onSearch"
        />

        <!-- Loading -->
        <div v-if="table_busy" class="text-center py-4">
            <font-awesome-icon :icon="['fas', 'spinner']" pulse size="2x" />
        </div>

        <!-- Aucun résultat -->
        <div v-else-if="filteredActes.length === 0" class="text-center py-4 text-muted">
            {{ $t('global.no_result') }}
        </div>

        <template v-else>
            <!-- Mobile : cards -->
            <div class="d-md-none">
                <div
                    v-for="acte in filteredActes"
                    :key="'card-' + acte.actes_id"
                    class="card mb-3"
                    :class="{ 'border-danger': acte.class === 'retard' }"
                >
                    <div class="card-body">
                        <div class="d-flex justify-content-between align-items-start mb-2">
                            <h6 class="card-title mb-0">{{ acte.actetype ? acte.actetype.actestype_label : '' }}</h6>
                            <small class="text-muted">{{ formatDate(acte.actes_date) }}</small>
                        </div>
                        <p class="card-text text-muted mb-2" v-if="!horse_id && acte.horse_nom">
                            <router-link :to="{ name: 'horseFiche', params: { horse_id: acte.horse_id }}">
                                <font-awesome-icon :icon="['fal', 'horse']" class="mr-1" />
                                {{ acte.horse_nom }}
                            </router-link>
                        </p>
                        <p class="card-text small mb-2" v-if="acte.reponse && acte.reponse.reponse_formatted_data">
                            {{ acte.reponse.reponse_formatted_data }}
                        </p>
                        <div v-if="hasNotes(acte)" class="text-right">
                            <a href="#" @click.prevent="openNotesModal(acte)" class="btn btn-sm btn-outline-primary">
                                <font-awesome-icon :icon="['fal', 'file-alt']" />
                                {{ $t('acte.notes') }} ({{ acte.notes.raw.length }})
                            </a>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Desktop : tableau -->
            <table class="table table-hover d-none d-md-table">
                <thead>
                    <tr>
                        <th class="sortable-col" @click="toggleSort('actes_date')">
                            {{ $t('acte.date') }}
                            <font-awesome-icon v-if="sortBy === 'actes_date'" :icon="['fal', sortDesc ? 'sort-down' : 'sort-up']" class="ml-1" />
                        </th>
                        <th v-if="!horse_id">{{ $t('acte.cheval') }}</th>
                        <th>{{ $t('acte.type') }}</th>
                        <th>{{ $t('acte.reponse') }}</th>
                        <th class="text-center">{{ $t('acte.notes') }}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="acte in filteredActes" :key="acte.actes_id" :class="acte.class">
                        <td class="text-nowrap">{{ formatDate(acte.actes_date) }}</td>
                        <td v-if="!horse_id">
                            <router-link 
                                v-if="acte.horse_id" 
                                :to="{ name: 'horseFiche', params: { horse_id: acte.horse_id }}"
                            >
                                {{ acte.horse_nom }}
                            </router-link>
                            <span v-else>-</span>
                        </td>
                        <td>{{ acte.actetype ? acte.actetype.actestype_label : '' }}</td>
                        <td>{{ acte.reponse ? acte.reponse.reponse_formatted_data : '' }}</td>
                        <td class="text-center">
                            <a 
                                v-if="hasNotes(acte)" 
                                href="#" 
                                @click.prevent="openNotesModal(acte)"
                                class="notes-link"
                            >
                                <span v-for="(font, key) in acte.notes.type_fonts" :key="key" class="mr-1">
                                    <font-awesome-icon :icon="font.font" /> {{ font.occurences }}
                                </span>
                            </a>
                            <span v-else>-</span>
                        </td>
                    </tr>
                </tbody>
            </table>
        </template>

        <!-- Modal de sélection/preview des notes -->
        <b-modal 
            ref="notesModal" 
            :title="$t('acte.notes')" 
            hide-footer 
            size="lg"
            centered
        >
            <!-- Liste des notes si plusieurs -->
            <div v-if="selectedNotes.length > 1 && !previewNote" class="list-group">
                <a 
                    v-for="note in selectedNotes" 
                    :key="note.note_id"
                    href="#"
                    class="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                    @click.prevent="loadNotePreview(note)"
                >
                    <div>
                        <strong>{{ note.note_title }}</strong>
                        <small class="text-muted d-block">{{ formatDate(note.note_date) }}</small>
                    </div>
                    <font-awesome-icon :icon="['fal', 'chevron-right']" />
                </a>
            </div>

            <!-- Preview d'une note -->
            <div v-else-if="previewNote">
                <div v-if="previewLoading" class="text-center py-5">
                    <font-awesome-icon :icon="['fas', 'spinner']" pulse size="2x" />
                </div>
                <template v-else>
                    <div class="mb-3" v-if="selectedNotes.length > 1">
                        <a href="#" @click.prevent="previewNote = null; previewUrl = null" class="btn btn-sm btn-outline-secondary">
                            <font-awesome-icon :icon="['fal', 'arrow-left']" /> {{ $t('global.retour') }}
                        </a>
                    </div>
                    <h6>{{ previewNote.note_title }}</h6>
                    <p class="text-muted small">{{ formatDate(previewNote.note_date) }}</p>
                    <div v-if="previewNote.note_comment" class="mb-3" v-html="sanitizeHtml(previewNote.note_comment)"></div>

                    <!-- Preview selon le type -->
                    <div v-if="previewUrl" class="preview-container">
                        <!-- Image -->
                        <img 
                            v-if="previewType === 'image'" 
                            :src="previewUrl" 
                            class="img-fluid rounded"
                            alt="Preview"
                        />
                        <!-- PDF -->
                        <iframe 
                            v-else-if="previewType === 'pdf'" 
                            :src="previewUrl" 
                            class="w-100 border rounded"
                            style="height: 500px;"
                        ></iframe>
                        <!-- Video -->
                        <video 
                            v-else-if="previewType === 'video'" 
                            :src="previewUrl" 
                            controls 
                            class="w-100 rounded"
                        ></video>
                        <!-- Audio -->
                        <audio 
                            v-else-if="previewType === 'audio'" 
                            :src="previewUrl" 
                            controls 
                            class="w-100"
                        ></audio>
                        <!-- Téléchargement pour les autres types -->
                        <div v-else class="text-center py-4">
                            <p class="text-muted mb-3">{{ $t('notes.preview_not_available') }}</p>
                            <a :href="previewUrl" :download="previewFileName" class="btn btn-primary">
                                <font-awesome-icon :icon="['fas', 'download']" class="mr-1" />
                                {{ $t('action.telecharger') }}
                            </a>
                        </div>
                    </div>
                    <div v-else class="text-muted text-center py-4">
                        {{ $t('notes.no_file') }}
                    </div>
                </template>
            </div>

            <!-- Note unique sans preview encore chargé -->
            <div v-else-if="selectedNotes.length === 1" class="text-center py-4">
                <font-awesome-icon :icon="['fas', 'spinner']" pulse size="2x" />
            </div>
        </b-modal>

        <a ref="downloadLink" href="#" hidden></a>
    </div>
</template>


<script type="text/javascript">
    import ActeMixin from "@/mixins/Actes.js"
    import NotesMixin from "@/mixins/Notes.js"
    import Navigation from "@/mixins/Navigation.js"
    import xss from 'xss'

    export default {
        name: "acteListe",
        mixins: [ActeMixin, NotesMixin, Navigation],
        props: {
            horse_id: { type: Number, default: 0 },
            actualTab: { type: String, default: '' }
        },
        data () {
            return {
                actes: [],
                table_busy: true,
                start_date: new Date(new Date().setMonth(new Date().getMonth() - 3)),
                end_date: new Date(),
                sortBy: 'actes_date',
                sortDesc: true,
                selectedNotes: [],
                previewNote: null,
                previewUrl: null,
                previewType: null,
                previewFileName: '',
                previewLoading: false,

                events_tab: {
                    'TableAction::notifyActs': this.notify
                }
            }
        },
        computed: {
            /**
             * Filtre les actes par onglet actif et les trie selon la colonne sélectionnée.
             * @returns {Array}
             */
            filteredActes() {
                let items = this.actes
                if (this.actualTab) {
                    items = items.filter(a => a.actetype && a.actetype.actestype_codegroupe === this.actualTab)
                }
                items = [...items].sort((a, b) => {
                    const valA = a[this.sortBy] || ''
                    const valB = b[this.sortBy] || ''
                    if (valA < valB) return this.sortDesc ? 1 : -1
                    if (valA > valB) return this.sortDesc ? -1 : 1
                    return 0
                })
                return items
            }
        },
        created() {
            this.onSearch()
        },
        methods: {

            /**
             * Lance la recherche des actes selon les dates sélectionnées.
             */
            onSearch() {
                this.actesDate(this.start_date.toDateInputValue(), this.end_date.toDateInputValue())
            },

            async actesDate(start_date, end_date) {
                this.table_busy = true

                if(this.horse_id > 0) {
                    const temp = await this.getActesFromHorse(this.horse_id).then(res => {
                        return this.formatActeDate(res)
                    })
                    this.actes = this.formatActesData(temp)
                }
                else {
                    this.actes = await this.getActes(start_date, end_date) || []
                }

                this.table_busy = false
            },

            /**
             * Bascule le tri sur la colonne donnée (asc/desc).
             * @param {string} column - Nom de la colonne de tri
             */
            toggleSort(column) {
                if (this.sortBy === column) {
                    this.sortDesc = !this.sortDesc
                } else {
                    this.sortBy = column
                    this.sortDesc = true
                }
            },

            /**
             * Vérifie si un acte possède des notes brutes.
             * @param {Object} acte
             * @returns {boolean}
             */
            hasNotes(acte) {
                return acte.notes && acte.notes.raw && acte.notes.raw.length > 0
            },

            /**
             * Ouvre la modal de notes pour un acte donné. Charge automatiquement la preview si une seule note.
             * @param {Object} acte
             */
            openNotesModal(acte) {
                this.selectedNotes = acte.notes.raw
                this.previewNote = null
                this.previewUrl = null
                this.previewType = null
                this.$refs.notesModal.show()

                // Si une seule note, charger directement la preview
                if (this.selectedNotes.length === 1) {
                    this.loadNotePreview(this.selectedNotes[0])
                }
            },

            /**
             * Charge les détails et la preview (fichier S3) d'une note.
             * @param {Object} note
             */
            async loadNotePreview(note) {
                this.previewNote = note
                this.previewLoading = true
                this.previewUrl = null
                this.previewType = null

                try {
                    // Charger les détails complets de la note (avec fichiers)
                    const noteDetails = await this.getNote(note.note_id)
                    
                    // Mettre à jour les infos de la note avec les détails complets
                    if (noteDetails.note_comment) {
                        this.previewNote = { ...note, note_comment: noteDetails.note_comment }
                    }
                    
                    if (noteDetails && noteDetails.files && noteDetails.files.length > 0) {
                        const file = noteDetails.files[0]
                        this.previewFileName = file.files_original_name || note.note_title || 'fichier'
                        
                        // Détecter le type depuis le mimetype ou l'extension du fichier
                        const mimeType = file.files_mimetype || this.getMimeFromFilename(file.files_original_name)
                        this.previewType = this.getPreviewType(mimeType)
                        
                        // Utiliser l'URL S3 directe pour la preview
                        if (file.files_s3_url) {
                            this.previewUrl = file.files_s3_url
                        } else {
                            // Fallback: télécharger le contenu
                            const content = await this.getNoteFileContent(note.note_id)
                            if (content && content.size > 0) {
                                this.previewUrl = window.URL.createObjectURL(new Blob([content]))
                            }
                        }
                    }
                } catch (error) {
                    this.failureToast('toast.info_save_failed')
                }

                this.previewLoading = false
            },

            /**
             * Sanitise du contenu HTML avant affichage via v-html.
             * @param {string} input
             * @returns {string}
             */
            sanitizeHtml(input) {
                return xss(input)
            },

            async notify(ids) {
                this.notifyClient(ids)
                .then(() => {
                    this.successToast('toast.notif_sent')
                })
            }
        },
        components: {
            DateRange: () => import('GroomyRoot/components/Inputs/DateRange')
        }
    }
</script>